import { useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "@/components/forge/Icon";
import { ForgeButton, IconButton } from "@/components/forge/ForgeButton";
import { HierarchyPicker } from "@/components/forge/HierarchyPicker";
import { Num } from "@/components/forge/primitives";
import { Sparkline } from "@/components/forge/Sparkline";
import { useTopline } from "@/features/operations/queries";
import { useSession } from "@/features/auth/mutations";
import { useAlertCounts } from "@/features/alerts/queries";
import { ROOT_ID } from "@/features/hierarchy/api";
import { useScope } from "@/providers/ScopeProvider";
import { useTheme } from "@/providers/ThemeProvider";
import { config } from "@/services/config";
import { queryKeys } from "@/services/queryClient";
import { formatDecimal, formatInteger, formatPercent } from "@/lib/format";
import { cn } from "@/lib/cn";
import defaultLogo from "@/assets/pragmatyc_logo.png";

/**
 * The 48px top bar. Never scrolls — an operator mid-triage must not lose the
 * scope selector or the shift they are on.
 *
 * Icon-only buttons dominate the end side deliberately: they are the only
 * controls immune to text expansion, and horizontal room here is the scarcest
 * in the product.
 */
/* The trigger's glyph. Region and sub-region are gone from the hierarchy
   entirely; their keys are removed rather than left answering for a tier no node
   can have. */
const SCOPE_ICON = {
  enterprise: "globe",
  site: "factory",
  line: "conveyor",
};

/**
 * The organisation's mark in the top bar.
 *
 * `logo_view_url` is presigned for five minutes and stripped from the cached
 * profile, so the first paint after a reload has no URL and a tab left open
 * long enough holds a dead one. Both fall back to the bundled default rather
 * than a broken image; the next `/auth/me` brings a fresh URL.
 */
function OrgLogo({ url, name, collapsed }) {
  const { t } = useTranslation("app");
  const client = useQueryClient();
  const [failed, setFailed] = useState(null);
  const lastRefresh = useRef(0);
  const src = url && failed !== url ? url : defaultLogo;

  /* A URL that fails to load has most likely expired. Ask `/auth/me` for a
     fresh one — at most once a minute, so a logo that is genuinely broken
     cannot turn into a request loop. */
  function onError() {
    setFailed(url);
    const now = Date.now();
    if (now - lastRefresh.current < 60_000) return;
    lastRefresh.current = now;
    client.invalidateQueries({ queryKey: queryKeys.session });
  }

  return (
    <img
      src={src}
      alt={name || t("settings.general.identity.logoAlt")}
      onError={onError}
      /* 180 × 28 is the box — about 6.4:1. The hint in Settings asks for 360 × 56,
         twice that, so it stays sharp on high-density screens. */
      className={cn("h-7 object-contain", collapsed ? "max-w-10" : "max-w-[180px]")}
    />
  );
}

export function TopBar({ railCollapsed, onToggleRail, onOpenAssistant }) {
  const { t } = useTranslation(["app", "common"]);
  const { theme, colourBlind, toggleTheme, toggleColourBlind, isDark } = useTheme();
  const { nodeId, params } = useScope();
  const { data: session } = useSession();
  const { data: topline } = useTopline(params);
  const { data: counts } = useAlertCounts(params);
  const [browsing, setBrowsing] = useState(false);

  /**
   * The scope panel reads one generation at a time, not the whole estate.
   *
   * ── What it used to do ───────────────────────────────────────────────────
   * Read `/assets/hierarchy` — the whole estate, every site and line — flatten
   * it in tree order with a depth per row, and render the lot. On this account that is a handful of rows. On a real one it is a read
   * of the entire hierarchy on every page load, for a panel most people never
   * open, rendered as a menu hundreds of rows long.
   *
   * ── What it does now ─────────────────────────────────────────────────────
   * Opens `HierarchyPicker` on the account, which fetches one level, paged and
   * searched by the server, and the next only when somebody goes into a row. A
   * session that never opens the panel makes no hierarchy request at all.
   *
   * It goes all the way down to the asset. The old tree stopped at the line, and
   * an operator asking "what is on this line" was left one level short.
   *
   * ── The root needs no request ────────────────────────────────────────────
   * The account has no endpoint under the hierarchy, and its name is already on
   * the session. So the panel opens with a row in it before anything is fetched.
   */
  const root = useMemo(
    () => ({
      id: ROOT_ID,
      tier: "enterprise",
      name: session?.account?.name ?? "",
      code: null,
      isActive: true,
    }),
    [session],
  );

  /* Nothing calls `setNodeId` today — the panel selects nothing (see below) — so
     this resolves to the root every time. Written as a lookup rather than as
     `root` so that restoring selection is a change to the panel and not to this
     line. */
  const scope = nodeId === ROOT_ID ? root : null;

  return (
    <header
      className={cn(
        "z-20 flex min-h-12 shrink-0 items-center border-b border-hairline bg-card pe-3.5",
        "dark:backdrop-blur-[20px] dark:backdrop-saturate-[1.8]",
      )}
    >
      {/* The organisation's logo, not the ForgeAI lockup — the same image
          Settings › General shows, uploaded logo or the bundled default.

          Points at the app home, not `/`. TopBar only mounts inside `/app/*`,
          so the person clicking this is signed in — sending them to the public
          landing at `/` reads as a sign-out (that page shows the Login CTA)
          and the browser back button then rewinds to whatever page they left,
          which is worse than not moving at all. */}
      <Link
        to="/app/home"
        className={cn(
          "flex shrink-0 items-center [transition:width_.18s_ease]",
          railCollapsed ? "w-15 justify-center ps-0" : "ps-4",
        )}
        style={{ width: railCollapsed ? "var(--rail-w-collapsed)" : "var(--rail-w)" }}
      >
        <OrgLogo url={session?.account?.logoUrl} name={session?.account?.name} collapsed={railCollapsed} />
      </Link>

      <IconButton bare label={railCollapsed ? t("common:nav.expand") : t("common:nav.collapse")} onClick={onToggleRail} className="size-8">
        <Icon name="rail" />
      </IconButton>

      {/* Scope. The account's real hierarchy, down to the asset.

          ── It opens; nothing inside it acts ──────────────────────────────
          "Where am I and what else is there" is a real question in the middle
          of triage, so the hierarchy is one press away. What it does not do is
          pretend the answer can be changed from here: selecting a node only
          ever altered the word in this bar, never what any screen below
          fetched (see ScopeProvider).

          It is the "Choose a location" dialog the anomaly filter and the user
          editor use, in `browse` mode — the same drill, search and paging, with
          the choosing taken out — rather than a tree of its own. Restoring
          selection is `single` and `setNodeId` the day scoped reads exist. */}
      {/* Control size, like the rest of the bar. The scope out-ranks the KPIs
          beside it by weight and by the coloured tier glyph, not by size —
          the prototype's larger figure is set in a taller bar and reads as a
          headline in a 48px one. */}
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={browsing}
        onClick={() => setBrowsing(true)}
        className="flex min-w-[236px] items-center gap-2 rounded-lg px-2.5 py-1 text-start transition-colors hover:bg-sunken max-lg:min-w-0"
      >
        <Icon name={SCOPE_ICON[scope?.tier] ?? "globe"} size="md" className="text-forge" />
        {/* The design reads "Enterprise · All Sites" at the root, and the
            qualifier is doing real work: the account name alone does not say
            whether you are looking at everything or at one plant that happens
            to share the company's name. Only at the root — on a site or a
            line the name IS the scope, and "Findlay Plant · All Sites" would
            be a contradiction. */}
        <span className="flex-1 truncate text-control">
          {scope?.name ? (
            scope.tier === "enterprise" ? (
              <>
                {scope.name} <span className="text-ink-dim">·</span> {t("scope.allSites")}
              </>
            ) : (
              scope.name
            )
          ) : (
            t("scope.unavailable")
          )}
        </span>
        <Icon name="down" size="sm" className="text-ink-mute" />
      </button>

      {browsing ? (
        <HierarchyPicker
          open
          browse
          onClose={() => setBrowsing(false)}
          title={t("scope.label")}
          root={{ ...root, name: root.name || t("scope.allSites") }}
        />
      ) : null}

      {/* OEE · MTBF · MTTR · Anomalies 24h — DEMO ONLY.
          `/kpis/topline` is answered by the fixture layer, so these four figures
          are authored rather than measured. A plant metric nobody measured, set
          in the top bar of every screen, is a number people quote in meetings —
          which is why this strip was removed from the shipping product.

          `VITE_DEMO_MODE` is exactly the case it should come back for: the flag
          says out loud that what is on screen is the prototype. With it off the
          bar is what ships. Delete the condition, not the strip, the day the
          endpoint is real. */}
      {/* ── How the strip gives way ──────────────────────────────────────
          Four figures, a sparkline, the scope selector, the shift chip and four
          buttons do not fit in a 48px bar on a 1024px laptop, so the strip sheds
          in the order the numbers matter.

          OEE and the 24h anomaly count survive to `lg`; MTBF and MTTR — the two
          nobody triages from — leave below `xl`. The whole strip goes below
          `lg`, where the bar is down to scope and controls.

          The previous single `max-xl:hidden` was why this appeared to be missing
          on a 1280-and-under window: it was rendering and hidden, rather than
          absent. Shedding per figure means the common laptop width keeps the two
          that are worth the space. */}
      {config.demoMode ? (
        <dl className="ms-1.5 flex min-w-0 items-center max-lg:hidden">
          <Kpi
            label={t("common:metrics.oee")}
            value={formatPercent(topline?.oee?.value)}
            trend={topline?.oee?.trend}
          />
          <Kpi
            className="max-xl:hidden"
            label={t("common:metrics.mtbf")}
            value={topline ? `${formatInteger(topline.mtbf.value)}h` : "—"}
          />
          <Kpi
            className="max-xl:hidden"
            label={t("common:metrics.mttr")}
            value={topline ? `${formatDecimal(topline.mttr.value)}h` : "—"}
          />
          <Kpi
            label={t("common:metrics.anomalies24h")}
            value={formatInteger(topline?.anomalies24h?.value)}
            tone={topline?.anomalies24h?.value > 15 ? "amber" : undefined}
          />
        </dl>
      ) : null}

      <div className="flex-1" />

      <div className="flex items-center gap-1.5">
        <IconButton label={isDark ? t("common:theme.toLight") : t("common:theme.toDark")} onClick={toggleTheme}>
          <Icon name={theme === "dark" ? "sun" : "moon"} />
        </IconButton>
        <IconButton label={t("common:theme.colourBlind")} pressed={colourBlind} onClick={toggleColourBlind}>
          <Icon name="eye" />
        </IconButton>
        {/* The bell went nowhere — no `onClick`, no destination, next to two
            toggles that work. The design points it at the alert-configuration
            screen, which is where the answer to "why did that fire, and who
            was told" lives. With the flag off there is no such screen, so it
            stays the inert control it was rather than becoming a broken link.

            The badge is the real critical count, not the design's literal 3. */}
        {config.demoMode ? (
          /* A Link rather than `IconButton asChild` — that component renders a
             `<button>` and does not take `asChild`, and a button that navigates
             is a link a keyboard user cannot open in a new tab. The classes are
             `IconButton`'s own so the three controls in this cluster stay one
             row of identical squares. */
          <Link
            to="/app/notifications"
            aria-label={t("common:nav.notifications", { count: counts?.critical ?? 0 })}
            className={cn(
              "relative flex size-[34px] items-center justify-center rounded-lg transition-colors",
              "border border-hairline bg-card text-ink-dim hover:border-forge hover:text-ink",
              "max-lg:size-11",
            )}
          >
            <Icon name="bell" />
            {counts?.critical ? (
              /* Aria-hidden: the count is already in the link's own label, and
                 a screen reader announcing "Notifications, 3 critical" then "3"
                 has read it twice. */
              <span
                aria-hidden="true"
                className="absolute top-0.5 flex size-4 items-center justify-center rounded-full bg-red text-[9px] font-bold text-white"
                style={{ insetInlineEnd: "2px" }}
              >
                <Num>{counts.critical}</Num>
              </span>
            ) : null}
          </Link>
        ) : (
          <IconButton label={t("common:nav.notifications", { count: counts?.critical ?? 0 })}>
            <Icon name="bell" />
          </IconButton>
        )}

        {/* The prototype's own `.btn-ai`, and now literally the same object as
            Investigate on an anomaly: `ForgeButton`'s `ai` variant carries the
            gradient, the weight, the 10px corner and the hover, so the product's
            two AI entry points cannot drift apart. This was hand-rolled here for
            months and was the only place the treatment was declared inline.

            See DESIGN.md correction 20 for why a violet-to-blue gradient ships on
            an AI affordance at all, given this document names that exact thing as
            the clearest generated-software tell there is. */}
        {/* Absent, not disabled, when the shell passes no handler: the assistant is
            behind a permission, and a greyed-out entry point advertises a feature
            this account cannot reach no matter what it does. */}
        {onOpenAssistant ? (
          <ForgeButton variant="ai" onClick={onOpenAssistant}>
            <Icon name="ai" size="sm" />
            <span className="max-lg:hidden">{t("common:brand.assistant")}</span>
          </ForgeButton>
        ) : null}
      </div>
    </header>
  );
}

/**
 * One figure in the top-bar strip. Demo mode only — see the block that renders
 * these.
 *
 * The sparkline sits BESIDE the number rather than under it, which is the
 * prototype's arrangement and the correct one for a 48px bar: stacking a chart
 * under a value would push the row past the bar's height, and the trend is a
 * gloss on the figure, not a second row of information.
 */
function Kpi({ label, value, tone, trend, className }) {
  return (
    <div className={cn("border-s border-subtle px-3 py-1 first:border-s-0", className)}>
      <dt className="text-label font-semibold tracking-[0.4px] whitespace-nowrap uppercase text-ink-dim">
        {label}
      </dt>
      <dd className="mt-0.5 flex items-center gap-2">
        <Num
          className="text-body-sm font-bold leading-none"
          style={tone ? { color: `var(--on-${tone})` } : undefined}
        >
          {value}
        </Num>
        {trend ? <Sparkline values={trend} /> : null}
      </dd>
    </div>
  );
}
